import React from 'react';
import { Modal, ModalProps } from '@mantine/core';

export interface AfaModalProps {
  /** Si el modal está abierto */
  opened: boolean;
  
  /** Callback para cerrar el modal */
  onClose: () => void;
  
  /** Título del modal */
  title?: React.ReactNode;
  
  /** Tamaño del modal */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | string;
  
  /** Contenido del modal */
  children: React.ReactNode;
  
  /** Si el modal está centrado verticalmente */
  centered?: boolean;
  
  /** Si mostrar el botón de cerrar */
  withCloseButton?: boolean;
  
  /** Props para el overlay */
  overlayProps?: ModalProps['overlayProps'];
  
  /** Props para las transiciones */
  transitionProps?: ModalProps['transitionProps'];
  
  /** Props para el botón de cerrar */
  closeButtonProps?: ModalProps['closeButtonProps'];
  
  /** Radio de los bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Padding del contenido */
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Si cerrar al hacer clic fuera */
  closeOnClickOutside?: boolean;
  
  /** Si cerrar al presionar Escape */
  closeOnEscape?: boolean;
  
  /** Callback al terminar la transición de salida */
  onExitTransitionEnd?: () => void;
  
  /** Callback al terminar la transición de entrada */
  onEnterTransitionEnd?: () => void;
  
  /** Z-index del modal (por defecto 1001, por encima del header) */
  zIndex?: number;
  
  /** Props adicionales para el Modal */
  [key: string]: any;
}

/**
 * Wrapper del Modal de Mantine con defaults de AFA y soporte para diferentes tamaños
 * 
 * Ejemplos de uso:
 * 
 * // Modal básico
 * <AfaModal opened={opened} onClose={close} title="Título">
 *   Contenido del modal
 * </AfaModal>
 * 
 * // Modal grande centrado
 * <AfaModal opened={opened} onClose={close} title="Título" size="lg" centered>
 *   Contenido del modal
 * </AfaModal>
 * 
 * // Modal full screen
 * <AfaModal opened={opened} onClose={close} title="Título" size="full">
 *   Contenido del modal
 * </AfaModal>
 * 
 * // Modal con tamaño personalizado
 * <AfaModal opened={opened} onClose={close} title="Título" size="70%">
 *   Contenido del modal
 * </AfaModal>
 */
export function AfaModal({
  opened,
  onClose,
  title,
  size = 'md',
  centered = false,
  withCloseButton = true,
  overlayProps,
  transitionProps,
  closeButtonProps,
  radius = 'md',
  padding = 'md',
  closeOnClickOutside = true,
  closeOnEscape = true,
  onExitTransitionEnd,
  onEnterTransitionEnd,
  zIndex = 1001,
  children,
  ...props
}: AfaModalProps) {
  
  // Si size es 'full', usar fullScreen prop de Mantine
  const isFullScreen = size === 'full';
  const modalSize = isFullScreen ? undefined : size;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      {...(modalSize && { size: modalSize })}
      fullScreen={isFullScreen}
      centered={centered}
      withCloseButton={withCloseButton}
      zIndex={zIndex}
      {...(overlayProps && { overlayProps })}
      transitionProps={
        transitionProps || {
          transition: isFullScreen ? 'fade' : 'pop',
          duration: 200,
        }
      }
      {...(closeButtonProps && { closeButtonProps })}
      radius={isFullScreen ? 0 : radius}
      padding={padding}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      {...(onExitTransitionEnd && { onExitTransitionEnd })}
      {...(onEnterTransitionEnd && { onEnterTransitionEnd })}
      styles={{
        title: {
          fontSize: 'var(--mantine-font-size-lg)',
          fontWeight: 500,
          color: 'var(--mantine-other-black)',
        
        },
        body: {
          fontSize: 'var(--mantine-font-size-sm)',
        },
      }}
      {...props}
    >
      {children}
    </Modal>
  );
}
